import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import ClipboardSearch from 'apollo-react-icons/ClipboardSearch';
import Download from 'apollo-react-icons/Download';
import classNames from 'classnames';
import noop from 'lodash/noop';
import qs from 'query-string';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import {
  openPopupWindowDialog,
  openWorkflowStepLink,
  setAttachmentPreview,
} from '../../../actions/EntityDisplayActions';
import Link from '../../../components/Link';
import { ENTITY_DISPLAY } from '../../../constants/Urls';
import { getUITA, isTrue } from '../../../utils/commonUtils';
import { FILE_SOURCES, ICON_CLICK_ACTIONS, LINK_TARGETS } from '../../constants/runtime.constants';
import {
  fetchAttachmentFromServer,
  isExternalLink,
  prependProtocol,
} from '../../utils/controls/ssHyperLink.utils';

const useStyles = makeStyles({
  root: {
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1.5,
    letterSpacing: 'normal',
    color: '#297dfd',
  },
  divSpacing: {
    display: 'flex !important',
    alignItems: 'center',
  },
  onRight: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  icon: {
    marginRight: 12,
  },
  disabled: {
    pointerEvents: 'none',
  },
  linkContainer: {
    display: 'flex',
    alignItems: 'center',
  },
});

const HyperLinkComponent = ({
  onRight,
  iconAction = ICON_CLICK_ACTIONS.PREVIEW,
  isEditor,
  hasIcon = true,
  asButton,
  queryString = '',
  pathname,
  target,
  text,
  isWorkflowStepLink,
  isAttachmentLink,
  ignoreRightMargin,
  disabled = false,
  isDocCompare,
  onClick: fetchFileFromServer,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const classes = useStyles();

  const query = qs.parse(queryString);
  const { filename, source, isDocumentContent, isPortalContent } = query;

  const isDownloadAction =
    iconAction === ICON_CLICK_ACTIONS.DOWNLOAD || source === FILE_SOURCES.DOCUMENT_COMPARE;
  const clickAction = isDownloadAction ? ICON_CLICK_ACTIONS.DOWNLOAD : iconAction;

  const handleAttachmentLink = async (e, action = ICON_CLICK_ACTIONS.DOWNLOAD) => {
    e.preventDefault();

    const { enableBrowserHandling } = qs.parse(location.search) || {};
    const preview = 'preview' in query ? isTrue(query.previw) : undefined;
    const isForDownload = isDocCompare
      ? true
      : action === ICON_CLICK_ACTIONS.DOWNLOAD
      ? isTrue(isDocumentContent) || isTrue(isPortalContent)
      : 'false';

    const params = {
      ...query,
      enableBrowserHandling,
      isForDownload,
      preview,
    };

    if (action === ICON_CLICK_ACTIONS.PREVIEW) {
      dispatch(setAttachmentPreview({ filename, params: query, isOpen: true }));
    } else {
      const attachmentText = filename || text;
      const downloadOptions = { ...params, fetchFileFromServer };
      fetchAttachmentFromServer(attachmentText, downloadOptions);
    }
  };

  const handleExternalLinkClick = (e) => {
    e.preventDefault();
    const { href, target } = e.target;
    const popupHandler =
      target === LINK_TARGETS.POPUP_WINDOW ? 'height=800,width=1200,popup' : undefined;
    window.open(href, target, popupHandler);
  };

  const handlePopupLink = (e) => {
    e.preventDefault();

    if (window.location.host === e.target.host) {
      dispatch(openPopupWindowDialog(`${e.target.href}&navs=0&targetBlank=0`));
    } else {
      window.open(e.target.href, 'PilgrimPopup', 'height=800,width=1200,popup');
    }
  };

  const handleWorkflowStepLink = (e) => {
    e.preventDefault();
    openWorkflowStepLink(`${queryString}&target=${target}`);
  };

  const handleLinkClick = (e) => {
    if (target === LINK_TARGETS.POPUP_WINDOW) {
      handlePopupLink(e);
      return;
    }

    const isEntityDisplayPage = RegExp(`${ENTITY_DISPLAY}$`, 'i').test(location.pathname);
    const isTargetWorkspace = target === LINK_TARGETS.WORKSPACE;

    const linkHandler = isAttachmentLink
      ? handleAttachmentLink
      : isWorkflowStepLink && isEntityDisplayPage && isTargetWorkspace
      ? handleWorkflowStepLink
      : noop;

    linkHandler(e);
  };

  return (
    <div ui-ta={getUITA('FormCtrl', 'SSHyperLink')}>
      <div
        className={classNames(
          isAttachmentLink ? classes.divSpacing : '',
          onRight ? classes.onRight : ''
        )}
      >
        {isAttachmentLink && hasIcon && asButton ? (
          <Button
            component={React.forwardRef((props, ref) => (
              <Link {...props} innerRef={ref} />
            ))}
            icon={isDownloadAction ? <Download /> : <ClipboardSearch />}
            to={{ pathname, search: queryString }}
            target={target}
            disabled={disabled}
            onClick={(e) => handleAttachmentLink(e, clickAction)}
            {...asButton}
          >
            {isEditor && (!text || text === 'null') ? 'Hyperlink' : text}
          </Button>
        ) : (
          <div className={classes.linkContainer}>
            {isAttachmentLink && hasIcon && !asButton && (
              <Link
                className={classNames({
                  [classes.root]: true,
                  [classes.icon]: !ignoreRightMargin,
                  [classes.disabled]: isEditor || disabled,
                })}
                to={{ pathname, search: queryString }}
                target={target}
                onClick={(e) => handleAttachmentLink(e, clickAction)}
                aria-label={pathname}
              >
                {isDownloadAction ? <Download /> : <ClipboardSearch />}
              </Link>
            )}
            {!isAttachmentLink && isExternalLink(pathname) ? (
              <a
                className={classNames({
                  [classes.root]: true,
                  [classes.icon]: !ignoreRightMargin,
                  [classes.disabled]: isEditor || disabled,
                })}
                href={`${prependProtocol(pathname)}${queryString ? `?${queryString}` : ''}`}
                target={target}
                rel="noopener noreferrer"
                onClick={handleExternalLinkClick}
                aria-label={pathname}
              >
                {isEditor && (!text || text === 'null') ? 'Hyperlink' : text}
              </a>
            ) : (
              <Link
                className={classNames({
                  [classes.root]: true,
                  [classes.icon]: !ignoreRightMargin,
                  [classes.disabled]: isEditor || disabled,
                })}
                to={{ pathname, search: queryString }}
                target={target}
                onClick={handleLinkClick}
                aria-label={pathname}
              >
                {isEditor && (!text || text === 'null') ? 'Hyperlink' : text}
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default HyperLinkComponent;
